import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { useCompany } from '@/company/context';
import { useAccountSettings } from '@/lib/account-settings';
import { useOptionalWorkspace } from '@/workspace/context';
import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import { actionSurfaceCopy } from './action-surface-copy';
import {
  messageApprovalCandidate,
  pendingActionScope,
  pendingActionStore,
} from './pending-action-store';

export function usePendingActions() {
  const workspace = useOptionalWorkspace();
  const settings = useAccountSettings();
  const company = useCompany();
  const scope = pendingActionScope(
    settings?.userId ?? workspace?.user?.id,
    company.data?.company?.id,
    workspace?.threadId,
  );
  const snapshot = useCallback(() => pendingActionStore.snapshot(scope), [scope]);
  return useSyncExternalStore(pendingActionStore.subscribe, snapshot, snapshot);
}

export function useApprovalPlaceholder() {
  const entries = usePendingActions();
  const copy = actionSurfaceCopy(useChatInterfaceLocale());
  const [clock, setClock] = useState(Date.now);
  useEffect(() => {
    const next = Math.min(
      ...entries.map(({ action }) => Date.parse(action.expiresAt)).filter((at) => at > Date.now()),
    );
    if (!Number.isFinite(next)) return;
    const timer = setTimeout(() => setClock(Date.now()), Math.max(0, next - Date.now()) + 1);
    return () => clearTimeout(timer);
  }, [entries, clock]);
  return messageApprovalCandidate(entries) ? copy.placeholder : null;
}

/** Navigation only. Approval stays on the exact action's review surface. */
export function PendingApprovalNotice() {
  const workspace = useOptionalWorkspace();
  const entries = usePendingActions();
  const copy = actionSurfaceCopy(useChatInterfaceLocale());
  const outside = entries.filter(
    (entry) => !entry.visible && entry.action.canDecide && entry.element?.isConnected,
  );
  if (workspace?.readOnly || !outside.length) return null;
  const focus = () => {
    const entry = outside[0];
    entry.element?.scrollIntoView({
      block: 'center',
      behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
    });
    // A card may mark its own decision control (an editable draft's first
    // button is a recipient's Remove); otherwise its first button.
    const target =
      entry.element?.querySelector('[data-pending-focus]') ??
      entry.element?.querySelector('button[data-variant="primary"], button');
    (target as HTMLButtonElement | null)?.focus({ preventScroll: true });
  };
  return (
    <button type="button" className="pc-pending-approval-notice" onClick={focus}>
      {copy.waitingCount(outside.length)} ↑
    </button>
  );
}
